import queryString from '../queryString.js';
import uniq from '../uniq.js';

/**
 * Transform fetched json to common usable template
 * @param {string} templateUrl
 * @param {object} json
 * @returns {object}
 */
export function transformTemplate(templateUrl, json) {
  let id;
  let version;
  let assetRootUrl;

  if (json.proxy) {
    // Template fetched through proxy server
    const { query } = queryString.parseUrl(templateUrl);

    id = query.product_id;
    version = query.version;
    assetRootUrl = json.proxy.assetRootUrl;
  } else {
    // Template fetched through S3 bucket
    const params = new URL(templateUrl).pathname.split('/').filter(i => !!i);

    id = params[0];
    version = params[1];
    assetRootUrl = templateUrl.split('/template.json')[0];
  }

  const origin = new URL(assetRootUrl).origin;

  return {
    ...json,
    id,
    version,
    templateUrl,
    assetRootUrl,
    origin,
    fonts: [],
  };
}

/**
 * Transform fetched text to list of fonts
 * @param {string} fontsRootUrl
 * @param {string} text
 * @returns {Array}
 */
export function transformFontList(fontsRootUrl, text) {
  const fonts = [];

  text.split('\n').forEach(font => {
    if (font) {
      const [name, ext] = font.split('.');
      if (['ttf', 'otf'].includes(ext.toLowerCase())) {
        fonts.push({
          name,
          url: `${fontsRootUrl}/${font}`,
        });
      }
    }
  });

  return fonts;
}

/**
 * Browse all cover and pages, and get list of defined fonts.
 * @param {object} template
 * @param {string} type - "all" || "cover" || "pages"
 * @param {Array} defaultFontNames
 * @returns {Array}
 */
export function getCustomFonts(template, type = 'all', defaultFontNames = []) {
  const pages = [];

  const useCover = type === 'all' || type === 'cover';
  const usePages = type === 'all' || type === 'pages';

  const customFonts = [...defaultFontNames];

  if (useCover && template.cover) {
    // TODO this font is used to spine by default
    customFonts.push('Baskerville-SemiBold');
    pages.push(...template.cover);
  }
  if (usePages && template.pages) {
    pages.push(...template.pages);
  }

  pages.forEach(page => {
    if (page.placeholders) {
      page.placeholders.forEach(placeholder => {
        if (placeholder.fontName) {
          customFonts.push(placeholder.fontName);
        }
      });
    }
  });

  if (template.template?.styles) {
    Object.entries(template.template.styles).forEach(([, styles]) => {
      Object.entries(styles ?? {}).forEach(([, style]) => {
        if (style?.fontName) {
          customFonts.push(style.fontName);
        }
      });
    });
  }

  let origin = template.origin;
  if (!origin) {
    origin = new URL(template.assetRootUrl ?? template.proxy.assetRootUrl).origin;
  }

  return uniq(customFonts).map(name => ({
    name,
    url: `${origin}/_fonts/${name}.ttf`,
  }));
}
